import React, { useState } from "react";
import { auth, db } from "../firebase"; 
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { Mail, Lock, ArrowRight, ShieldCheck, Fingerprint, Command, Activity, Database, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

const LoginPage = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });

const handleLogin = async (e) => {
  e.preventDefault();
  setIsSyncing(true);
  try {
    const userCredential = await signInWithEmailAndPassword(auth, loginData.email, loginData.password);
    const user = userCredential.user;

    // Check if professional profile exists in Firestore
    const docRef = doc(db, "users", user.uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // Profile complete -> Go to engine
      window.location.href = "/engine";
    } else {
      // Auth exists but profile is missing -> Go to complete profile
      window.location.href = "/completeprofile";
    }

  } catch (error) {
    const msg = encodeURIComponent("Authentication Failed or Free Trial over. Please login to rescan and generate reports.");
    window.location.href = `/?message=${msg}`;
  } finally {
    setIsSyncing(false);
  }
};

const handleGoogleLogin = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if professional profile exists in Firestore
    const docRef = doc(db, "users", user.uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      window.location.href = "/engine";
    } else {
      // New Google users won't have a record yet, so send them to onboarding
      window.location.href = "/completeprofile";
    }

  } catch (error) {
    console.error("Google Auth Failed", error);
    const msg = encodeURIComponent("Login failed. Your free trial is over; please login to continue report generation.");
    window.location.href = `/?message=${msg}`;
  }
};

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center font-sans antialiased text-slate-900 overflow-hidden">
      
      <div className="w-full min-h-screen flex flex-col md:flex-row">
        
        {/* --- LEFT SECTION: BRAND IDENTITY (KEPT AS REQUESTED) --- */}
 <div className="hidden md:flex md:w-1/2 bg-[#020617] p-12 lg:p-20 flex-col justify-between relative overflow-hidden">
  {/* --- DYNAMIC SCANNER BACKGROUND --- */}
  <div className="absolute inset-0 z-0">
    {/* Moving Scanner Beam */}
    <div className="absolute top-0 w-full h-[1px] bg-blue-500/40 shadow-[0_0_15px_rgba(59,130,246,0.3)] animate-scan-line z-20"></div>
    
    {/* Technical Grid Overlay */}
    <div className="absolute inset-0 opacity-[0.03] z-10" 
         style={{ backgroundImage: 'linear-gradient(#3b82f6 1px, transparent 1px), linear-gradient(90deg, #3b82f6 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
    </div>

    {/* Indication/Defect Spots - Timed to the scanner pass */}
    {/* These use 'animate-indication' which fades in/out based on the 4s scan cycle */}
    <div className="absolute top-[25%] left-[40%] flex flex-col items-center gap-1 animate-indication-slow">
        <div className="w-6 h-3 bg-red-500/20 rounded-full blur-md"></div>
        <div className="w-2 h-2 bg-red-400/40 rounded-full"></div>
    </div>

    <div className="absolute top-[55%] left-[25%] flex flex-col items-center gap-1 animate-indication-fast">
        <div className="w-8 h-4 bg-orange-500/20 rounded-full blur-md"></div>
        <div className="w-2 h-2 bg-orange-400/40 rounded-full"></div>
    </div>

    <div className="absolute top-[80%] left-[60%] flex flex-col items-center gap-1 animate-indication-medium">
        <div className="w-5 h-2 bg-blue-400/20 rounded-full blur-md"></div>
        <div className="w-1.5 h-1.5 bg-blue-400/40 rounded-full"></div>
    </div>
  </div>

  <div className="relative z-30">
    {/* Branding */}
    <div className="flex items-center gap-3 mb-16">
      <div className="w-10 h-10 bg-blue-600 rounded flex items-center justify-center">
        <Command className="text-white" size={24} />
      </div>
      <span className="text-white font-bold tracking-tight text-xl italic">EVAL<span className="text-blue-500 not-italic">NDT</span></span>
    </div>

    <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-8 tracking-tighter">
      Precision inspection <br /> 
      <span className="text-slate-500">at scale.</span>
    </h1>

    {/* Features List */}
    <div className="space-y-8 mt-12">
      <div className="flex items-start gap-4 group">
        <div className="mt-1 p-2 bg-slate-800/50 border border-slate-700 rounded-md text-blue-400">
          <Database size={18} />
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider">Automated C-Scan Reporting</h4>
          <p className="text-slate-500 text-sm mt-1 leading-relaxed max-w-xs">Transform raw inspection data into professional PDF reports in seconds.</p>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="mt-1 p-2 bg-slate-800/50 border border-slate-700 rounded-md text-blue-400">
          <Users size={18} />
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider">Personnel Certification</h4>
          <p className="text-slate-500 text-sm mt-1 leading-relaxed max-w-xs">Track NDT Level certifications and renewal dates for your entire team.</p>
        </div>
      </div>

      <div className="flex items-start gap-4">
        <div className="mt-1 p-2 bg-slate-800/50 border border-slate-700 rounded-md text-blue-400">
          <Activity size={18} />
        </div>
        <div>
          <h4 className="text-white font-semibold text-sm uppercase tracking-wider">Real-time Data Sync</h4>
          <p className="text-slate-500 text-sm mt-1 leading-relaxed max-w-xs">Cloud-based management ensuring field data reaches the office instantly.</p>
        </div>
      </div>
    </div>
  </div>

  {/* Footer Status */}
  <div className="relative z-30 border-t border-slate-800 pt-8 flex items-center justify-between">
    <div className="flex items-center gap-2">
       <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
       <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Inspection Engine Online</span>
    </div>
    <span className="text-[10px] text-slate-600 font-mono tracking-tighter">V4.2.0-STABLE</span>
  </div>

  <style>
    {`
      @keyframes scan {
        0% { top: 0%; opacity: 0; }
        5% { opacity: 1; }
        95% { opacity: 1; }
        100% { top: 100%; opacity: 0; }
      }

      /* Indication Keyframes: Appearing when scan line is near */
      @keyframes detect {
        0%, 15% { opacity: 0; transform: scale(0.8); }
        25%, 35% { opacity: 1; transform: scale(1); }
        45%, 100% { opacity: 0; }
      }

      @keyframes detect-mid {
        0%, 45% { opacity: 0; transform: scale(0.8); }
        55%, 65% { opacity: 1; transform: scale(1); }
        75%, 100% { opacity: 0; }
      }

      @keyframes detect-late {
        0%, 70% { opacity: 0; transform: scale(0.8); }
        80%, 90% { opacity: 1; transform: scale(1); }
        95%, 100% { opacity: 0; }
      }

      .animate-scan-line {
        animation: scan 4s linear infinite;
      }

      .animate-indication-slow {
        animation: detect 4s linear infinite;
      }

      .animate-indication-fast {
        animation: detect-mid 4s linear infinite;
      }

      .animate-indication-medium {
        animation: detect-late 4s linear infinite;
      }
    `}
  </style>
</div>
        {/* --- RIGHT SECTION: UPDATED PROFESSIONAL LOGIN --- */}
        <div className="w-full md:w-1/2 bg-white flex flex-col justify-center p-8 lg:p-24 relative">
          <div className="max-w-[400px] mx-auto w-full">
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-2">Workspace Login</h2>
              <p className="text-slate-500 text-sm leading-relaxed">
                Enter your authorized credentials to access the NDT data portal.
              </p>
            </div>

            {/* SSO / Google Integration */}
            <button 
              onClick={handleGoogleLogin}
              className="w-full h-12 border border-slate-200 rounded-xl flex items-center justify-center gap-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all mb-6"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
              Sign in with Google
            </button>

            <div className="relative mb-8 text-center">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-100"></span></div>
              <span className="relative bg-white px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Or corporate mail</span>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider ml-1">Email Identity</label>
                <div className="relative group">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                  <input 
                    type="email" 
                    placeholder="name@focusndt.com" 
                    className="w-full h-14 pl-12 pr-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all text-sm font-medium"
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    required 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Access Key</label>
                  <button type="button" className="text-[11px] font-bold text-blue-600 hover:text-blue-800 transition-colors">
                    Reset
                  </button>
                </div>
                <div className="relative group">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" />
                  <input 
                    type="password" 
                    placeholder="••••••••••••" 
                    className="w-full h-14 pl-12 pr-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-600 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all text-sm font-medium"
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    required 
                  />
                </div>
              </div>

              <button 
                disabled={isSyncing}
                className="w-full h-14 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-4 shadow-lg shadow-blue-100 disabled:opacity-50"
              >
                {isSyncing ? "Authorizing..." : "Initialize Dashboard"} 
                <ArrowRight size={18} />
              </button>
            </form>

            <div className="mt-10 text-center border-t border-slate-50 pt-8">
              <p className="text-sm text-slate-500 font-medium">
                New to EvalNDT? <Link to="/signup" className="text-blue-600 font-bold hover:underline underline-offset-4">Register License</Link>
              </p>
            </div>

            {/* Compliance Footer */}
            <div className="mt-12 flex items-center justify-center gap-8 opacity-40 grayscale transition-all hover:grayscale-0 cursor-default">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-blue-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest">ISO 27001</span>
              </div>
              <div className="flex items-center gap-2">
                <Fingerprint size={16} className="text-blue-600" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Multi-Auth</span>
              </div>
            </div>
          </div>
        </div>

      </div>

      <style>
        {`
          @keyframes scan {
            0% { top: -5%; }
            100% { top: 105%; }
          }
          .animate-scan-line {
            animation: scan 4s linear infinite;
          }
        `}
      </style>
    </div>
  );
};

export default LoginPage;